/* 네이버 블로그 붙여넣기용 — 글은 실제 글자로(검색에 잡힌다), 템플릿 사진 배치는 표로 옮긴다
     [맨 위] 시공사례 요약 글 → 공사 개요표(현장·위치·공간)
     [페이지마다] 제목·글(h2·h3·문단) · 색 띠 글(배경색 칸) · 사진 배치 표(칸 합치기로 크기가 다른 사진까지) · 사진 설명 — 페이지 사이는 구분선
     [맨 아래] #해시태그 — 네이버가 본문 해시태그를 태그 칸에 자동으로 넣는다
   네이버 에디터 붙여넣기 실측(2026-10-29): 표(colspan·rowspan·칸 배경색)·h2/h3·인용구·구분선·글자 색/크기/정렬은 남고,
   한 문단에 여러 장 나란히·flex·위치 지정·겹침은 지워진다(사진이 세로로 쌓임). 그래서 나란히 놓인 사진은 표 칸에 넣는다.
   사진은 공개 주소여야 한다 — 네이버가 붙여넣은 외부 사진을 자기 서버로 가져가기 때문(인증 주소는 못 가져감) */
import { roomLabel } from './constants';
import { refOf } from './images';
import { isSamplePhoto } from './samples';
import type { El, Page } from './types';

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const escLines = (s: string) => esc(s).replace(/\n/g, '<br>');

/** 장식용 짧은 영문 글(예: INTERIOR CASE STUDY, BEFORE, 01)은 뺀다 */
const isDecor = (t: string) => t.length < 2 || /^[A-Z0-9\s·.,&/:+#-]{1,40}$/.test(t);
const isHeading = (e: El) => (e.fontSize || 16) >= 26;
const byPos = (a: El, b: El) => (Math.abs(a.y - b.y) > 12 ? a.y - b.y : a.x - b.x);

/** 한 페이지의 글 요소 — 위에서 아래, 왼쪽에서 오른쪽 순서. 장식 글·같은 글은 뺀다 */
function pageTextEls(p: Page): { el: El; text: string; heading: boolean }[] {
  const seen = new Set<string>();
  return p.elements
    .filter((e) => e.type === 'text' && (e.text || '').trim())
    .slice()
    .sort(byPos)
    .map((e) => ({ el: e, text: (e.text || '').trim(), heading: isHeading(e) }))
    .filter((t) => !isDecor(t.text) && !seen.has(t.text) && (seen.add(t.text), true));
}

/** 한 페이지의 글 — 위에서 아래, 왼쪽에서 오른쪽 순서 */
export function pageTexts(p: Page): { text: string; heading: boolean }[] {
  return pageTextEls(p).map(({ text, heading }) => ({ text, heading }));
}

/** 요약을 만들 재료 — 페이지마다 "N페이지: 글…" (AI 요약 프롬프트·요약 실패 시 대신 쓰는 글) */
export function allTexts(pages: Page[]): string {
  return pages.map((p, i) => {
    const t = pageTexts(p).map((x) => x.text).join(' ');
    return t ? `${i + 1}페이지: ${t}` : '';
  }).filter(Boolean).join('\n');
}

/** AI 요약이 안 될 때 — 본문 글(제목이 아닌 글)을 앞에서부터 400자 정도 */
export function fallbackSummary(pages: Page[]): string {
  const body = pages.flatMap((p) => pageTexts(p).filter((t) => !t.heading).map((t) => t.text)).join(' ');
  return body.length > 400 ? body.slice(0, 400).replace(/\s+\S*$/, '') + '…' : body;
}

const isPhoto = (e: El) => e.type === 'image' && !!e.src && !isSamplePhoto(e.src);

/** 시공 사진 — 페이지 순서, 페이지 안에서는 위→아래·왼쪽→오른쪽. 같은 사진은 한 번만, 템플릿 예시 사진은 뺀다 (사진 ZIP) */
export function pagePhotoSrcs(pages: Page[]): string[] {
  const seen = new Set<string>(), out: string[] = [];
  for (const p of pages) {
    for (const e of p.elements.filter(isPhoto).slice().sort(byPos)) {
      const r = refOf(e);
      const key = r?.aiImgId ? 'ai-' + r.aiImgId : r?.externalId || e.src!;
      if (!seen.has(key)) { seen.add(key); out.push(e.src!); }
    }
  }
  return out;
}

/* ── 블로그용 사진 칸 ── 템플릿 칸 비율로 자른 사진을 올린다(표 칸 높이가 맞아야 배치가 그대로 보인다) */
export interface BlogFrame { key: string; src: string; w: number; h: number }
/** 같은 사진·같은 칸 비율이면 한 장만 올린다 */
export const frameKey = (e: El) => `${e.src}|${Math.round((e.w / Math.max(1, e.h)) * 50)}`;

/** 블로그에 올릴 사진 칸 — 페이지 순서대로, 중복 없이 */
export function blogFrames(pages: Page[]): BlogFrame[] {
  const seen = new Set<string>(), out: BlogFrame[] = [];
  for (const p of pages) {
    for (const e of p.elements.filter(isPhoto).slice().sort(byPos)) {
      const key = frameKey(e);
      if (!seen.has(key)) { seen.add(key); out.push({ key, src: e.src!, w: e.w, h: e.h }); }
    }
  }
  return out;
}

/* ── 현장 정보 → 개요표·해시태그 ── */
export interface BlogFacts { siteName?: string; address?: string }

/** 현장 이름에서 동·호수는 뺀다 — "OO아파트 101동 1203호" → "OO아파트" */
const placeName = (s?: string) => (s || '').replace(/\s*\d+\s*(동|호|층).*$/, '').trim();
/** 주소에서 동네 — 법정동(○○동·읍·면)이 있으면 그것, 없으면 구·시 */
function regionOf(address?: string): string {
  const t = (address || '').replace(/\(.*?\)/g, ' ').split(/\s+/).filter(Boolean);
  return t.filter((x) => /[가-힣](동|읍|면)$/.test(x) && !/^\d/.test(x)).pop()
    || t.filter((x) => /[가-힣]구$/.test(x)).pop() || t.filter((x) => /[가-힣]시$/.test(x)).pop() || '';
}
const roomsOf = (pages: Page[]) => [...new Set(pages.map((p) => p.room).filter((r): r is string => !!r && r !== 'ETC'))];

/** 본문 끝 해시태그 — 현장·동네·공간으로. 네이버 태그는 띄어쓰기·특수문자가 안 된다 */
export function blogTags(pages: Page[], facts: BlogFacts): string[] {
  const clean = (s: string) => s.replace(/[^0-9A-Za-z가-힣]/g, '');
  const place = clean(placeName(facts.siteName)), region = clean(regionOf(facts.address));
  const tags = [
    place && `${place}인테리어`, region && `${region}인테리어`, place && `${place}리모델링`,
    ...roomsOf(pages).map((r) => `${clean(roomLabel(r))}리모델링`),
    '인테리어시공사례', '리모델링',
  ].filter((t): t is string => !!t && t.length >= 2);
  return [...new Set(tags)].slice(0, 12);
}

/* ── 페이지 → HTML ── */
const CAPTION = 'font-size:13px;color:#888888'; // dq-ignore: 네이버 블로그에 붙여넣는 HTML — 우리 CSS 토큰이 없는 곳이라 값으로 쓴다
const TABLE = '<table border="0" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:none;width:100%"><tbody>';
const TD = 'border:none;padding:2px'; // dq-ignore: 네이버 블로그에 붙여넣는 HTML — 우리 CSS 토큰이 없는 곳이라 값으로 쓴다
const BLOG_W = 760;
/** 표 칸 배경으로 쓸 수 있는 색만(테마 변수·그라데이션은 붙여넣으면 사라진다) */
const solid = (c?: string) => (c && /^(#[0-9a-f]{3,8}|rgba?\([^)]*\))$/i.test(c.trim()) ? c.trim() : '');

interface Box { el: El; x0: number; y0: number; x1: number; y1: number }
const boxOf = (el: El): Box => ({ el, x0: el.x, y0: el.y, x1: el.x + el.w, y1: el.y + el.h });
const inside = (b: Box, x: number, y: number) => x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1;
const overlapArea = (a: Box, b: Box) => Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)) * Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));
const area = (b: Box) => (b.x1 - b.x0) * (b.y1 - b.y0);

/** 가까운 값(칸 사이 여백 30px 이내)은 한 선으로 — 템플릿 사진 사이 여백이 빈 칸으로 생기지 않게 */
function snapEdges(vals: number[]): number[] {
  const out: number[] = [];
  for (const v of [...vals].sort((a, b) => a - b)) if (!out.length || v - out[out.length - 1] > 30) out.push(v);
  return out;
}
const nearestIdx = (edges: number[], v: number) => edges.reduce((bi, e, i) => (Math.abs(e - v) < Math.abs(edges[bi] - v) ? i : bi), 0);

type Photo = (e: El) => string | undefined;

function imgTag(url: string, width: number, alt: string) {
  return `<img src="${esc(url)}" width="${Math.round(width)}" alt="${esc(alt)}">`;
}
function textCell(texts: { text: string; heading: boolean }[]) {
  return texts.map((t) => (t.heading ? `<b>${escLines(t.text)}</b>` : escLines(t.text))).join('<br><br>');
}

/** 사진 묶음(세로로 겹치는 사진들 + 그 옆 글) → 표. 칸이 겹치는 자유 배치는 표로 못 옮겨서 한 장씩 아래로 */
function bandHtml(boxes: Box[], side: { box: Box; text: string; heading: boolean }[], photo: Photo, alt: string): string {
  // 옆 글은 위아래에 다른 칸이 없으면 사진 묶음 높이만큼 늘린다 — 글 위아래로 빈 칸이 생기지 않게
  const by0 = Math.min(...boxes.map((b) => b.y0)), by1 = Math.max(...boxes.map((b) => b.y1));
  const hOverlap = (a: Box, b: Box) => Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0) > 0;
  // 옆 글 좌우 끝은 가까운(80px 이내) 사진 끝선에 맞춘다 — 글 상자가 사진보다 조금 넓어 사진 옆에 빈 칸이 생기지 않게
  const photoXs = boxes.flatMap((b) => [b.x0, b.x1]);
  const toPhotoX = (v: number) => { const n = photoXs.reduce((a, x) => (Math.abs(x - v) < Math.abs(a - v) ? x : a), photoXs[0]); return Math.abs(n - v) <= 80 ? n : v; };
  const sides = side.map((s) => {
    const x = { ...s.box, x0: toPhotoX(s.box.x0), x1: toPhotoX(s.box.x1) };
    const box = x.x1 > x.x0 ? x : s.box;
    const stretch = ![...boxes, ...side.filter((o) => o !== s).map((o) => o.box)].some((o) => hOverlap(o, box));
    return { ...s, box: stretch ? { ...box, y0: by0, y1: by1 } : box };
  });
  const items = [...boxes.map((b) => ({ box: b, side: null as null | typeof side[number] })), ...sides.map((s) => ({ box: s.box, side: s }))];
  const stack = () => boxes.map((b) => { const u = photo(b.el); return u ? `<p>${imgTag(u, BLOG_W, alt)}</p>` : ''; }).join('')
    + side.map((s) => (s.heading ? `<h3>${escLines(s.text)}</h3>` : `<p>${escLines(s.text)}</p>`)).join('');
  if (boxes.length === 1 && !side.length) {
    const u = photo(boxes[0].el);
    return u ? `<p>${imgTag(u, BLOG_W, alt)}</p>` : '';
  }
  for (let i = 0; i < items.length; i++) for (let j = i + 1; j < items.length; j++) {
    if (overlapArea(items[i].box, items[j].box) > 0.1 * Math.min(area(items[i].box), area(items[j].box))) return stack();
  }
  const xs = snapEdges(items.flatMap((it) => [it.box.x0, it.box.x1]));
  const ys = snapEdges(items.flatMap((it) => [it.box.y0, it.box.y1]));
  const cols = xs.length - 1, rows = ys.length - 1;
  if (cols < 1 || rows < 1) return stack();
  const owner: (number | null)[][] = Array.from({ length: rows }, () => Array<number | null>(cols).fill(null));
  const span = items.map((it, k) => {
    const c0 = nearestIdx(xs, it.box.x0), c1 = nearestIdx(xs, it.box.x1), r0 = nearestIdx(ys, it.box.y0), r1 = nearestIdx(ys, it.box.y1);
    return { k, c0, c1, r0, r1 };
  });
  for (const s of span) {
    if (s.c1 <= s.c0 || s.r1 <= s.r0) return stack();
    for (let r = s.r0; r < s.r1; r++) for (let c = s.c0; c < s.c1; c++) {
      if (owner[r][c] !== null) return stack();
      owner[r][c] = s.k;
    }
  }
  // 어느 줄에도 쓰이지 않는 칸(사진 사이 넓은 여백)은 표에서 뺀다 — 합친 칸 수도 남는 칸만 센다
  const keepC = Array.from({ length: cols }, (_, c) => owner.some((row) => row[c] !== null));
  const keepR = owner.map((row) => row.some((x) => x !== null));
  const kept = (keep: boolean[], from: number, to: number) => keep.slice(from, to).filter(Boolean).length;
  const keptW = (from: number, to: number) => { let w = 0; for (let c = from; c < to; c++) if (keepC[c]) w += xs[c + 1] - xs[c]; return w; };
  const keptTotal = Math.max(1, keptW(0, cols));
  let html = TABLE;
  for (let r = 0; r < rows; r++) {
    if (!keepR[r]) continue;
    let tr = '';
    for (let c = 0; c < cols; c++) {
      if (!keepC[c]) continue;
      const k = owner[r][c];
      if (k === null) { tr += `<td style="${TD}"></td>`; continue; }
      const s = span[k];
      if (s.r0 !== r || s.c0 !== c) continue;
      const cs = kept(keepC, s.c0, s.c1), rs = kept(keepR, s.r0, s.r1);
      const attrs = `${cs > 1 ? ` colspan="${cs}"` : ''}${rs > 1 ? ` rowspan="${rs}"` : ''}`;
      const it = items[k];
      // 칸 폭을 템플릿 비율로 못 박는다 — 안 그러면 글이 긴 칸이 폭을 가져가 사진이 작아진다
      const spanW = keptW(s.c0, s.c1), pct = Math.round((spanW / keptTotal) * 100);
      if (it.side) {
        tr += `<td${attrs} width="${pct}%" style="${TD};width:${pct}%;padding:8px 12px;vertical-align:middle">${textCell([it.side])}</td>`; // dq-ignore: 네이버 블로그에 붙여넣는 HTML — 우리 CSS 토큰이 없는 곳이라 값으로 쓴다
      } else {
        const u = photo(it.box.el), w = spanW * (BLOG_W / keptTotal) - 4;
        tr += `<td${attrs} width="${pct}%" style="${TD};width:${pct}%;vertical-align:middle">${u ? imgTag(u, w, alt) : ''}</td>`;
      }
    }
    html += `<tr>${tr}</tr>`;
  }
  return html + '</tbody></table>';
}

/** 한 페이지 → HTML. 위에서 아래 순서로 글 · 색 띠 글 · 사진 배치 표를 놓는다 */
function pageHtml(p: Page, photo: Photo, facts: BlogFacts): string {
  const place = placeName(facts.siteName);
  const alt = [place, p.room ? roomLabel(p.room) : '', '시공 사진'].filter(Boolean).join(' ');
  const photos = p.elements.filter(isPhoto).map(boxOf).sort((a, b) => a.y0 - b.y0);
  // 세로로 겹치는 사진끼리 한 묶음(한 표)
  const bands: { y0: number; y1: number; boxes: Box[]; side: { box: Box; text: string; heading: boolean }[]; over: { el: El; text: string; heading: boolean }[] }[] = [];
  for (const b of photos) {
    const last = bands[bands.length - 1];
    if (last && b.y0 < last.y1 - 8) { last.boxes.push(b); last.y1 = Math.max(last.y1, b.y1); }
    else bands.push({ y0: b.y0, y1: b.y1, boxes: [b], side: [], over: [] });
  }
  // 색 띠 — 글 아래 깔린 도형(페이지 전체 바탕은 빼고)
  const shapes = p.elements.filter((e) => e.type === 'shape' && solid(e.bg) && (e.kind || 'rect') === 'rect' && e.w * e.h < 720 * 960 * 0.5).map(boxOf);
  const blocks: { y: number; html: string }[] = [];
  let headingDone = false;
  for (const t of pageTextEls(p)) {
    const b = boxOf(t.el), cx = (b.x0 + b.x1) / 2, cy = (b.y0 + b.y1) / 2;
    const onPhoto = bands.find((bd) => bd.boxes.some((x) => inside(x, cx, cy)));
    if (onPhoto) { onPhoto.over.push(t); continue; } // 사진 위에 얹은 글(표지 제목 등)은 그 사진 바로 앞에 글로
    const besidePhoto = bands.find((bd) => cy > bd.y0 && cy < bd.y1 && !bd.boxes.some((x) => overlapArea(x, b) > 0));
    if (besidePhoto && !t.heading) { besidePhoto.side.push({ box: b, text: t.text, heading: false }); continue; }
    const band = solid(t.el.bg) || solid(shapes.find((s) => inside(s, cx, cy))?.el.bg);
    let html: string;
    if (band) {
      const color = solid(t.el.color) || '#ffffff'; // dq-ignore: 네이버 블로그에 붙여넣는 HTML — 우리 CSS 토큰이 없는 곳이라 값으로 쓴다
      html = `${TABLE}<tr><td style="border:none;padding:16px;text-align:center;background:${band};color:${color}"><b>${escLines(t.text)}</b></td></tr></tbody></table>`; // dq-ignore: 네이버 블로그에 붙여넣는 HTML — 우리 CSS 토큰이 없는 곳이라 값으로 쓴다
    } else if (t.heading) {
      html = headingDone ? `<h3>${escLines(t.text)}</h3>` : `<h2>${escLines(t.text)}</h2>`;
      headingDone = true;
    } else html = `<p>${escLines(t.text)}</p>`;
    blocks.push({ y: t.el.y, html });
  }
  // 사진 설명은 페이지의 마지막 사진 묶음 아래 한 번만
  bands.forEach((bd, i) => {
    const over = bd.over.map((t) => (t.heading ? `<h2>${escLines(t.text)}</h2>` : `<p>${escLines(t.text)}</p>`)).join('');
    const table = bandHtml(bd.boxes, bd.side, photo, alt);
    const caption = table && p.room && i === bands.length - 1 ? `<p style="text-align:center"><span style="${CAPTION}">${esc(alt)}</span></p>` : '';
    blocks.push({ y: bd.y0, html: over + table + caption });
  });
  return blocks.sort((a, b) => a.y - b.y).map((b) => b.html).join('');
}

/** 블로그 본문 HTML·글. photoUrl — 사진 칸(frameKey) → 올린 공개 주소. 제목은 네이버 제목 칸에 따로 넣으므로 본문에 넣지 않는다 */
export function buildBlog(o: { pages: Page[]; summary: string; facts: BlogFacts; photoUrl: Map<string, string> }): { html: string; text: string } {
  const photo: Photo = (e) => o.photoUrl.get(frameKey(e));
  const tags = blogTags(o.pages, o.facts);
  let html = '';
  for (const para of o.summary.split(/\n+/).map((s) => s.trim()).filter(Boolean)) html += `<p>${esc(para)}</p>`;
  const rooms = roomsOf(o.pages).map((r) => roomLabel(r));
  const facts = [
    ['현장', placeName(o.facts.siteName)],
    ['위치', regionOf(o.facts.address)],
    ['시공 공간', rooms.join(' · ')],
  ].filter(([, v]) => v);
  if (facts.length) {
    html += '<p><br></p><table border="1" style="border-collapse:collapse;width:100%"><tbody>'
      + facts.map(([k, v]) => `<tr><th style="width:30%">${esc(k)}</th><td>${esc(v)}</td></tr>`).join('') + '</tbody></table>';
  }
  o.pages.forEach((p, i) => {
    const body = pageHtml(p, photo, o.facts);
    if (body) html += (i || html ? '<hr>' : '') + body;
  });
  if (tags.length) html += `<p><br></p><p>${tags.map((t) => '#' + esc(t)).join(' ')}</p>`;
  const text = [
    o.summary,
    ...o.pages.map((p) => pageTexts(p).map((t) => t.text).join('\n')).filter(Boolean),
    tags.map((t) => '#' + t).join(' '),
  ].filter(Boolean).join('\n\n');
  return { html, text };
}
